/**
 * Rent Receipts list (v2.5.6)
 *
 * Landlord-visible history of every receipt issued for a tenant. Feeds
 * from `GET /api/receipts/tenant/:tenantId` — the receipt is generated
 * automatically after every payment (webhook + manual + resolve-pending
 * paths) and emailed to the tenant on issue.
 *
 * Rendered inside PropertyDetail, one card per tenant. If a property has
 * no tenants there's nothing to render.
 */
import React, { useEffect, useState } from 'react';
import { receiptsService } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';

interface Receipt {
    receiptId: string;
    paymentId: string;
    amount: number;
    paidDate: string;
    periodYM: string;
    method?: string;
    externalRef?: string | null;
    fileName?: string;
    contentHash?: string;
    issuedAt: string;
}

interface Props {
    tenantId: string;
    tenantName?: string;
}

const fmtPeriod = (ym: string): string => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '—';
    const [y, m] = ym.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const COLLAPSE_KEY = 'receiptsList.collapsed';

const ReceiptsList: React.FC<Props> = ({ tenantId, tenantName }) => {
    const isTexas = getAppMarket() === 'texas';
    const [receipts, setReceipts] = useState<Receipt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    });
    const toggleCollapsed = () => {
        setCollapsed((c) => {
            const next = !c;
            try { window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
            return next;
        });
    };

    useEffect(() => {
        if (!tenantId) return;
        let mounted = true;
        setLoading(true); setError(null);
        receiptsService.list(tenantId)
            .then((res: any) => {
                if (!mounted) return;
                const list = res.data?.data || res.data || [];
                setReceipts(Array.isArray(list) ? list : []);
            })
            .catch((err: any) => {
                if (!mounted) return;
                setError(err?.response?.data?.message || err?.message || 'Failed to load receipts');
            })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [tenantId]);

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <button
                type="button"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                aria-controls={`receipts-body-${tenantId}`}
                className="w-full flex items-center justify-between text-left group"
            >
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <i className="fas fa-file-invoice text-emerald-600 mr-2"></i>
                    Rent Receipts{tenantName ? <span className="ml-2 text-sm font-normal text-gray-500">— {tenantName}</span> : null}
                </h3>
                <span className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{receipts.length} receipt{receipts.length === 1 ? '' : 's'}</span>
                    <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'} text-gray-400 group-hover:text-gray-600`}></i>
                </span>
            </button>

            {collapsed ? null : (
            <div id={`receipts-body-${tenantId}`} className="mt-4">
            {loading ? (
                <div className="text-sm text-gray-400 italic">Loading…</div>
            ) : error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>
            ) : receipts.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl">
                    <p className="text-sm text-gray-400">No receipts issued yet.</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Receipts are generated and emailed automatically when a payment lands.
                    </p>
                </div>
            ) : (
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase tracking-wide">
                            <th className="py-2 pr-3">Period</th>
                            <th className="py-2 pr-3">Paid</th>
                            <th className="py-2 pr-3 text-right">Amount</th>
                            <th className="py-2 pr-3">Method</th>
                            <th className="py-2 pr-3">Receipt</th>
                            <th className="py-2 text-right">PDF</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {receipts.map((r) => (
                            <tr key={r.receiptId}>
                                <td className="py-2 pr-3 text-gray-900">{fmtPeriod(r.periodYM)}</td>
                                <td className="py-2 pr-3 text-gray-600">{r.paidDate ? new Date(r.paidDate).toLocaleDateString(isTexas ? 'en-US' : 'en-GB') : '—'}</td>
                                <td className="py-2 pr-3 text-right font-semibold text-gray-900">{isTexas ? '$' : '£'}{Number(r.amount || 0).toFixed(2)}</td>
                                <td className="py-2 pr-3 text-gray-600 capitalize">{(r.method || '—').replace(/_/g, ' ')}</td>
                                <td className="py-2 pr-3 text-[11px] font-mono text-gray-500 truncate max-w-[160px]" title={r.receiptId}>{r.receiptId}</td>
                                <td className="py-2 text-right">
                                    <a
                                        href={receiptsService.downloadUrl(r.receiptId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold hover:bg-emerald-100 transition"
                                    >
                                        <i className="fas fa-download mr-1"></i>PDF
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            </div>
            )}
        </div>
    );
};

export default ReceiptsList;
